import { useEffect, useState, useMemo, useRef } from "react";
import { Grid } from "@mui/material";
import styles from "./styles.module.scss";
import { Heading } from "../modules/molecules/heading/heading";
import { useTableContext } from "../context/tableContext";
import _dashboardLinks from "../layout/sidebar.links";
import { HeaderV1 } from "gf-ui-components-mui-lib";
import { useFileUploadContext } from "../context/fileUploadContext";
import { FilterKeywords } from "../modules/molecules/filterKeywords/filterKeywords";
import { RenderFilters } from "../modules/organisms/filterWindow/renderFilters";
import { RoleFilter } from "../modules/organisms/filterWindow/roleFilter";
import { UpdateStatus } from "../modules/molecules/updateStatusModal/updateStatus";
import { FilterHeader } from "../modules/molecules/filterRender/FilterHeader";
import { ClientWindow } from "../modules/organisms/generateClient/clientWindow";
import { GenerateHeader } from "../modules/molecules/generateScreen/header";
import { FileUploadStatus } from "../modules/atoms/UploadModal/FileStatus";
import { downloadExportData } from "../services/downloadTemplate";
import { exportAll } from "../enums/endpoints";
import exportCSV from "@/assets/svg/Export all.svg";
import exportCSVWhite from "@/assets/img/export-csv-contained.svg";
import SuccessIcon from "@/assets/svg/info-circle.svg";
import FailedIcon from "@/assets/svg/failed.svg";
import ErrorIcon from "@/assets/svg/error.svg";
import UploadCloud from "@/assets/svg/upload-cloud.svg";
import UploadCloudSecond from "@/assets/svg/upload-cloud-02.svg";
import DeleteIconImage from "@/assets/img/delete.svg";
import { headerButtonLinks, toolbarButtonLinks } from "../layout/button.links";
import MultipleSelectCheckmarks from "../modules/molecules/CustomFilter";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import useUserHook from "../pages/User/userHook";
import {
  WorkspaceSidebar,
  WORKSPACE_ACCENT_DEVICE_MANAGER,
  NavLink,
} from "./sidebar";
import { PRODUCT_LOGOS } from "../constants/productLogos";
import { useProductList } from "../hooks/useProductList";
import GroupSVG from "../assets/svg/group.svg";
import { useAuth } from "src/hooks/useLogin";

// XSM-1536: SSO Login
function MainLayout() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [select, setSelect] = useState("inventory2");
  const location = useLocation();
  const navigate = useNavigate();
  const {
    rowsPerPage,
    selectedRows,
    tableData,
    chargerValue,
    showGenerate,
    applyFilter,
    setPaginationBottom,
    checked,
    connectors,
    filterModel,
    filterStatus,
    filterTenant,
    connectorType,
    filterOcpp,
    qrFlag,
    showNext,
    selectedWareHouseRows,
    setChargerValue,
    statusList,
    setShowNext,
    nextButton,
    generateButton,
    generateFlag,
    setGenerateFlag,
    arrowBackButton,
    closeButton,
    resetFilters,
    setOpenFilter,
    openFilter,
    updatedWareHouseData,
    setUpdateStatusWindow,
    updateStatusWindow,
    setApplyFilter,
    selectedRowsWithPage,
    selectedClients,
    handleChangeClient,
    clientinfo,
    setUserValue,
    userValue,
    applyRole,
    applyEmail,
    setApplyRole,
    setApplyEmail,
    uniqueEmails,
    uniqueRoles,
  } = useTableContext();
  const {
    uploadView,
    handleFileUpload,
    setUploadView,
    successWindow,
    failedWindow,
    errorWindow,
    setSuccessWindow,
    setFailedWindow,
    uploadProgress,
  } = useFileUploadContext();
  const { handleLogout } = useAuth();
  const { createNewUser, permissionAccess } = useUserHook();
  const [filteredLinks, setFilteredLinks] = useState<NavLink[]>([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const { products, hasSingleProduct } = useProductList();
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Core Device Manager nav items are always visible.
    // Permission gating for user-management lives outside this sidebar list.
    setFilteredLinks(_dashboardLinks);
  }, []);

  const selectedItem = useMemo(() => {
    const links = filteredLinks.map((link) => link.url).filter(Boolean) as string[];
    const sortedLinks = [...links].sort((a, b) => b.length - a.length);
    return sortedLinks.find(
      (link) =>
        location.pathname === link || location.pathname.startsWith(`${link}/`)
    );
  }, [location.pathname, filteredLinks]);

  useEffect(() => {
    if (!filteredLinks.length) return;
    const matchingLink = filteredLinks.find(
      (link) =>
        link.url &&
        (location.pathname === link.url ||
          location.pathname.startsWith(`${link.url}/`))
    );
    if (matchingLink && matchingLink.id !== select) {
      setSelect(matchingLink.id);
    }
  }, [filteredLinks, location.pathname, select]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showProductDropdown &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProductDropdown]);

  const handleSidebarSelection = (url: string) => {
    if (url !== location.pathname) {
      navigate(url);
    }
  };

  const handleProductSelect = (item: { link?: string }) => {
    if (item?.link) {
      window.location.href = item.link;
    }
    setShowProductDropdown(false);
  };

  const changeTheProducts = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (products.length > 0) {
      setShowProductDropdown(!showProductDropdown);
    } else {
      navigate("/dashboard");
    }
  };

  const [filterPresent, setFilterPresent] = useState(0);
  useEffect(() => {
    if (
      applyFilter &&
      (checked?.length > 0 ||
        connectorType?.length > 0 ||
        filterStatus?.length > 0 ||
        filterModel !== "" ||
        filterTenant !== "" ||
        connectors !== "" ||
        qrFlag !== "" ||
        filterOcpp !== "")
    ) {
      setPaginationBottom(true);
    }

    if (applyFilter && tableData?.length < rowsPerPage) {
      setPaginationBottom(false);
    }

    if (!applyFilter) {
      setPaginationBottom(false);
    }

    if (checked?.length > 0) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (checked?.length === 2) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (filterStatus?.length > 0) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (filterStatus?.length === 2) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (connectorType?.length > 0) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (connectorType?.length === 2) {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (connectors !== "") {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (filterModel !== "") {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (filterOcpp !== "") {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (filterTenant !== "") {
      setFilterPresent((prev: any) => prev + 1);
    }

    if (qrFlag !== "") {
      setFilterPresent((prev: any) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applyFilter,
    checked,
    connectors,
    filterModel,
    filterStatus,
    filterTenant,
    connectorType,
    filterOcpp,
    qrFlag,
  ]);

  const isAuthenticated = () => {
    const isLoggedIn = localStorage.getItem("dm_isLoggedIn");
    return isLoggedIn === "true";
  };

  return (
    <Grid container className={`${styles.App}`}>
      {isAuthenticated() ? (
        <>
          <div ref={sidebarRef}>
            <WorkspaceSidebar
              navItems={filteredLinks}
              selectedItem={selectedItem}
              onSelect={handleSidebarSelection}
              workspaceName="Device Manager"
              workspaceIconSrc={PRODUCT_LOGOS.deviceManager}
              workspaceAccentColor={WORKSPACE_ACCENT_DEVICE_MANAGER}
              showProductSwitcher={products.length > 0}
              groupIcon={
                <img src={GroupSVG} alt="workspace switcher" height={20} width={20} />
              }
              isProductDropdownOpen={showProductDropdown}
              productItems={products}
              onProductSwitcherClick={changeTheProducts}
              onProductSelect={handleProductSelect}
              onSettingsClick={() =>
                handleLogout({ redirectToLogoutEndpoint: hasSingleProduct })
              }
              settingsLabel={hasSingleProduct ? "Logout" : "Exit"}
            />
          </div>
          <Grid className={`${styles.tableContainer}`}>
            <HeaderV1
              xsmadmin={false}
              sx={{
                background: "transparent",
                boxShadow: "none",
                position: "static",
                paddingLeft: "0px",
                padding: 0,
                "@media (max-width: 1440px)": {
                  "& .MuiToolbar-root": {
                    minHeight: "20px",
                    height: 30,
                    marginTop: "5px",
                    fontSize: "16px !important",
                  },
                },
              }}
              tenantSelect={select === "inventory2" ? true : false}
              tenantDropdown={
                <MultipleSelectCheckmarks
                  {...{ selectedClients, handleChangeClient, clientinfo }}
                />
              }
              logo={
                <Heading
                  title={
                    select === "user-management"
                      ? "User Management"
                      : "Device Manager"
                  }
                />
              }
              setChargerValue={setChargerValue}
              statusList={statusList}
              showNext={showNext}
              setShowNext={setShowNext}
              showGenerate={showGenerate}
              nextButton={nextButton}
              generateButton={generateButton}
              generateFlag={generateFlag}
              setGenerateFlag={setGenerateFlag}
              arrowBackButton={arrowBackButton}
              closeButton={closeButton}
              chargerValue={chargerValue}
              checked={checked}
              connectors={connectors}
              connectorType={connectorType}
              qrFlag={qrFlag}
              filterStatus={filterStatus}
              filterTenant={filterTenant}
              filterOcpp={filterOcpp}
              filterModel={filterModel}
              tableData={tableData}
              resetFilters={resetFilters}
              setOpenFilter={setOpenFilter}
              openFilter={openFilter}
              applyFilter={applyFilter}
              // uploadView={uploadView}
              handleFileUpload={handleFileUpload}
              // setUploadView={setUploadView}
              successWindow={successWindow}
              failedWindow={failedWindow}
              errorWindow={errorWindow}
              setSuccessWindow={setSuccessWindow}
              setFailedWindow={setFailedWindow}
              FilterKeywords={<FilterKeywords />}
              RenderFilters={(applyRole && <RoleFilter />) || <RenderFilters />}
              UpdateStatus={<UpdateStatus />}
              FilterHeader={<FilterHeader />}
              ClientWindow={<ClientWindow />}
              setUpdateStatusWindow={setUpdateStatusWindow}
              updateStatusWindow={updateStatusWindow}
              permissionAccess={permissionAccess}
              GenerateHeader={
                <GenerateHeader
                  showNext={showNext}
                  showGenerate={showGenerate}
                  generateButton={generateButton}
                  arrowBackButton={arrowBackButton}
                  closeButton={closeButton}
                  nextButton={nextButton}
                  selectedRowsLength={selectedRows?.length}
                />
              }
              FileUploadStatusError={
                <FileUploadStatus
                  imageIcon={ErrorIcon}
                  status={"Failed to Upload File"}
                  displayMessage={errorWindow?.message}
                />
              }
              FileUploadStatusSuccess={
                <FileUploadStatus
                  imageIcon={SuccessIcon}
                  status={"Success!!"}
                  displayMessage={successWindow?.message}
                />
              }
              FileUploadStatusFailed={
                <FileUploadStatus
                  imageIcon={FailedIcon}
                  status={"Something went wrong!"}
                  displayMessage={failedWindow?.message}
                />
              }
              downloadExportData={downloadExportData}
              exportAll={exportAll}
              exportCSV={exportCSV}
              exportCSVWhite={exportCSVWhite}
              SuccessIcon={SuccessIcon}
              FailedIcon={FailedIcon}
              headerButtonLinks={
                location.pathname === "/user-management"
                  ? []
                  : headerButtonLinks
              }
              toolbarButtonLinks={toolbarButtonLinks}
              uploadProgress={uploadProgress}
              UploadCloud={UploadCloud}
              UploadCloudSecond={UploadCloudSecond}
              DeleteIconImage={DeleteIconImage}
              selectedWareHouseRows={selectedWareHouseRows}
              selectedRows={selectedRows}
              setApplyFilter={setApplyFilter}
              selectedRowsWithPage={selectedRowsWithPage}
              isUser={location.pathname === "/user-management" ? true : false}
              createUser={createNewUser}
              setUserValue={setUserValue}
              userValue={userValue}
              setApplyRole={setApplyRole}
              setApplyEmail={setApplyEmail}
              applyRole={applyRole}
              applyEmail={applyEmail}
              uniqueEmails={uniqueEmails}
              uniqueRoles={uniqueRoles}
            />
            <Outlet />
          </Grid>
        </>
      ) : (
        <Navigate to="/" replace />
      )}
    </Grid>
  );
}

export default MainLayout;
